import config from '../../config';
import { authHeader } from '../_helpers';

const QUERY_TIMEOUT_MS = 185000;

function parseResponseBody(response) {
    return response.text().then((text) => {
        if (!text) return null;
        try {
            return JSON.parse(text);
        } catch (error) {
            return { detail: text };
        }
    });
}

function responseDetail(data, fallback) {
    if (!data) return fallback;
    if (typeof data.detail === 'string') return data.detail;
    if (typeof data.error === 'string') return data.error;
    return fallback;
}

function authenticatedHeaders(headers) {
    const auth = authHeader();
    return Object.assign(
        {},
        headers,
        auth.Authorization ? { Authorization: auth.Authorization } : {}
    );
}

async function getStatus() {
    const response = await fetch(`${config.apiUrl}/chat/status`, {
        method: 'GET',
        headers: authenticatedHeaders({ Accept: 'application/json' }),
    });
    const data = await parseResponseBody(response);

    if (!response.ok) {
        throw {
            kind: 'http',
            status: response.status,
            detail: responseDetail(data, 'Unable to check AI chat availability'),
        };
    }

    return data || { enabled: false };
}

async function askQuestion(userId, question, signal) {
    const controller = new AbortController();
    let externalAbortHandler;
    let timedOut = false;

    if (signal) {
        if (signal.aborted) {
            controller.abort();
        } else {
            externalAbortHandler = () => controller.abort();
            signal.addEventListener('abort', externalAbortHandler, { once: true });
        }
    }

    const timeoutId = window.setTimeout(() => {
        timedOut = true;
        controller.abort();
    }, QUERY_TIMEOUT_MS);

    try {
        const response = await fetch(`${config.apiUrl}/chat/module-query`, {
            method: 'POST',
            headers: authenticatedHeaders({
                'Content-Type': 'application/json',
                Accept: 'application/json',
            }),
            body: JSON.stringify({
                user_id: String(userId),
                question,
            }),
            signal: controller.signal,
        });
        const data = await parseResponseBody(response);

        if (!response.ok) {
            throw {
                kind: 'http',
                status: response.status,
                detail: responseDetail(data, 'The AI request failed'),
                error: data && data.error,
            };
        }

        return data;
    } catch (error) {
        if (error && error.kind) throw error;
        if (error && error.name === 'AbortError') {
            if (timedOut) {
                throw { kind: 'network', detail: 'The AI request timed out' };
            }
            throw { kind: 'aborted', detail: 'The AI request was cancelled' };
        }
        throw { kind: 'network', detail: error && error.message ? error.message : 'Network error' };
    } finally {
        window.clearTimeout(timeoutId);
        if (signal && externalAbortHandler) {
            signal.removeEventListener('abort', externalAbortHandler);
        }
    }
}

function reportFileName(reportPath) {
    return String(reportPath || '').split(/[\\/]/).filter(Boolean).pop() || 'report';
}

async function downloadReport(reportPath) {
    const fileName = reportFileName(reportPath);
    const response = await fetch(
        `${config.apiUrl}/chat/reports/download/${encodeURIComponent(fileName)}`,
        {
            method: 'GET',
            headers: authenticatedHeaders({ Accept: '*/*' }),
        }
    );

    if (!response.ok) {
        const data = await parseResponseBody(response);
        throw {
            kind: 'http',
            status: response.status,
            detail: responseDetail(data, 'Report download failed'),
        };
    }

    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(objectUrl);

    return fileName;
}

export const aiChatService = {
    getStatus,
    askQuestion,
    downloadReport,
    reportFileName,
};
